<template>
    <div
        :class="[
            {
                disabled: isLoading,
                'draggable-is-active': draggableActive,
            },
            'position-relative row row-lg-large row-xl-large'
        ]"
    >
        <slot />

        <div
            v-if="isLoading"
            class="spinner-holder"
        >
            <ui-spinner />
        </div>

        <div
            v-for="column in columns"
            :key="column.id"
            class="col-md-6 col-lg-3"
        >
            <h4 class="d-flex align-items-center justify-content-between mb-4">
                <span class="d-flex align-items-center font-weight-bold">
                    {{ column.title }}

                    <ui-icon
                        icon="info"
                        :content="column.title_tooltip"
                        class="ml-2"
                        v-tippy
                    />
                </span>
                <span>
                    {{ column.items.length }}
                </span>
            </h4>

            <draggable
                @change="statusChange($event, column.id)"
                @end="draggableActive = false"
                @start="draggableActive = true"
                :list="column.items"
                :animation="250"
                group="items"
                class="position-relative h-100 columns"
            >
                <kanban-card
                    v-for="(item, index) in column.items"
                    :key="index"
                    :data="item"
                    class="mb-4"
                />
            </draggable>
        </div>

        <portal to="portal">
            <ui-modal
                v-if="modal === 'kanbanNote'"
                class="modal-small"
                :title="$t('patients.notes.title')"
                @close="cancelMove"
            >
                <form
                    @submit.prevent="confirmMove"
                    action="#"
                    method="POST"
                    class="d-flex flex-column"
                >
                    <p
                        v-html="$t('patients.notes.message', { user: userCurrent.name })"
                    />

                    <ui-textarea
                        v-model="note"
                        :label="$tc('messages.messages', 1)"
                        rows="5"
                        required
                    />

                    <div class="d-flex align-items-center justify-content-between">
                        <button
                            @click="cancelMove"
                            type="button"
                            class="btn btn-gray"
                        >
                            {{ $t('general.cancel') }}
                        </button>

                        <button class="btn btn-primary">
                            <span>
                                {{ $t('general.save') }}
                            </span>
                        </button>
                    </div>
                </form>
            </ui-modal>
        </portal>
    </div>
</template>

<script>
    import { mapMutations, mapState } from 'vuex';

    import axios from 'axios';
    import Draggable from 'vuedraggable';

    import KanbanCard from './card';

    import UiIcon from '../ui/icon.vue';
    import UiModal from '../ui/modal.vue';
    import UiSpinner from '../ui/spinner.vue';
    import UiTextarea from '../ui/textarea';

    export default {
        name: 'KanbanBoard',
        components: {
            Draggable,
            KanbanCard,
            UiIcon,
            UiModal,
            UiSpinner,
            UiTextarea,
        },
        props: {
            hospitalId: {
                type: String,
                default: null,
            },
            url: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                columns: [],
                draggableActive: false,
                isLoading: false,
                note: null,
                userCurrent: {},
                eventCurrent: {},
                columnIdCurrent: null,
            };
        },
        computed: {
            ...mapState('filters', {
                originalQuery: 'query',
            }),

            ...mapState('modal', [
                'modal',
            ]),
        },
        methods: {
            ...mapMutations('modal', [
                'setModal',
            ]),

            async confirmMove() {
                try {
                    this.isLoading = true;

                    await axios.post(this.route('hospitals.patients.kanban.status_change', this.hospitalId), {
                        card: this.eventCurrent.added.element,
                        column: this.columnIdCurrent,
                    });

                    await axios.post(this.route('hospitals.patients.kanban.add_note', this.hospitalId), {
                        note: this.note,
                        user: this.userCurrent,
                    });

                    this.$notify({
                        title: `${this.$t('general.notify.status_changed_for')} ${this.userCurrent.name}`,
                        type: 'success',
                    });

                    this.setModal(null);
                } catch(e) {
                    this.$notify({
                        title: this.$t('general.notify.something_went_wrong'),
                        type: 'danger',
                    });
                } finally {
                    this.note = null;
                    this.isLoading = false;
                }
            },

            async cancelMove() {
                this.setModal(null);

                await this.fetchItems();
            },

            async fetchItems() {
                this.isLoading = true;

                try {
                    const { data } = await axios.post(this.url, {
                        ...this.originalQuery,
                    });

                    this.columns = data;

                } finally {
                    this.isLoading = false;
                }
            },

            async statusChange(event, columnId) {
                if (!event.added) return;

                this.userCurrent = event.added.element;
                this.eventCurrent = event;
                this.columnIdCurrent = columnId;

                this.setModal('kanbanNote');
            },
        },
        watch: {
            originalQuery: {
                handler() {
                    this.fetchItems();
                },
                immediate: true,
            },
        },
    }
</script>

<style lang="scss" scoped>
    .card[draggable='true'] {
        cursor: move;
    }

    .columns {
        &::before {
            position: absolute;
            top: 0;
            left: 0;
            width: calc(100% + 14px);
            height: calc(100% - 44px);
            margin: -7px;
            content: '';
            border: 2px dashed $gray;
            border-radius: $radius;
            opacity: 0;
            transition: 0.3s;

            .draggable-is-active & {
                opacity: 1;
            }
        }
    }

    .spinner-holder {
        z-index: 2;
    }
</style>
